import { type LoaderFunctionArgs, json } from '@remix-run/node'
import { Link, useLoaderData } from '@remix-run/react'
import { Grid } from '#app/components/layout/grid.tsx'
import { H2 } from '#app/components/typography/h2.tsx'
import { P } from '#app/components/typography/p.tsx'
import { Code } from '#app/old_components/typography/code.tsx'
import { sessionKey } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { authSessionStorage } from '#app/utils/session.server.ts'
import { readSource } from '../sections/sections.server.tsx'
import { Description, Features, Source, type componentProps, Parts, Usage } from '../sections/sections.tsx'

export const handle = {
	breadcrumb: 'Grid',
}

const component: componentProps = {
	name: 'Grid',
	shortName: 'Grid',
	fileName: 'grid',
	category: 'layout',
	description: ['Component for creating bidimensional grid layouts.'],
	features: ['Removes inaccessible CSS features.', 'Inherits the space scale on the gapX and gapY props.'],
	parts: [
		{
			name: 'Grid',
			description: 'Component for creating bidimensional grid layouts.',
			props: [
				{ name: 'inline', type: 'boolean', default: 'false', description: 'Whether the grid is inline or not.' },
				{ name: 'cols', type: '"number"', default: '1', description: 'The number of columns.' },
				{ name: 'rows', type: '"number"', default: '1', description: 'The number of rows.' },
				{ name: 'justify', type: "'start' | 'center' | 'end' | 'between'", default: "'start'", description: 'The horizontal alignment of the grid.' },
				{ name: 'align', type: "'start' | 'center' | 'end' | 'stretch'", default: "'start'", description: 'The vertical alignment of the grid.' },
				{ name: 'gapX', type: '0 to 20', default: '0', description: 'The horizontal gap between the grid items.' },
				{ name: 'gapY', type: '0 to 20', default: '0', description: 'The vertical gap between the grid items.' },
			],
		},
	],
	usage: `<Grid cols="3" rows="3" justify="stretch" align="stretch" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="start" align="start" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="center" align="start" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="end" align="start" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="start" align="center" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="center" align="center" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="end" align="center" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="start" align="end" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="center" align="end" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>
<Grid cols="3" rows="3" justify="end" align="end" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
	<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
</Grid>`,
}

export const loader = async ({ request }: LoaderFunctionArgs) => {
	const authSession = await authSessionStorage.getSession(request.headers.get('cookie'))
	const sessionId = authSession.get(sessionKey)
	const session =
		sessionId ?
			await prisma.session.findUnique({
				select: { user: { select: { id: true, paid: true } } },
				where: { id: sessionId, expirationDate: { gt: new Date() } },
			})
		:	null
	const source = session?.user.paid ? await readSource({ category: component.category, fileName: component.fileName }) : null
	return json({ source })
}

const HeadingRoute = () => {
	const { source } = useLoaderData<typeof loader>()

	const { name, description, shortName, fileName, features, usage, parts } = component

	return (
		<>
			<Description name={name} description={description} />
			<Features features={features} />
			<Parts parts={parts} />

			<H2>Notes</H2>
			<P>
				If you set <Code>inline</Code>, <Code>justify</Code> has no meaning because the <Code>{'<Grid />'}</Code> has the least width possible.
			</P>
			<P>
				Setting <Code>align</Code>, only has meaning if the <Code>{'<Grid />'}</Code> has a height. Otherwise it will have the least height possible.
			</P>
			<P>
				If you set <Code>justify="stretch"</Code>, the children shouldn't have a fixed width. Otherwise they won't stretch.
			</P>
			<P>
				If you set <Code>align="stretch"</Code>, the children shouldn't have a fixed height. Otherwise they won't stretch.
			</P>
			<P>
				Setting <Code>justify="stretch"</Code> has the same effect as setting <Code>w-full</Code> on all the children (assuming <Code>flow="row"</Code>).
			</P>
			<P>
				Setting <Code>align="stretch"</Code> has the same effect as setting <Code>h-full</Code> on all the children (assuming <Code>flow="row"</Code>).
			</P>

			<H2>Accessibility</H2>
			<P>
				Changing visual order creates a disconnect between content and presentation and is, therefore,{' '}
				<Link to="/articles/the-dark-side-of-the-grid" className="underline">
					bad for accessibility
				</Link>
				. For this reason, I've ommitted <Code>grid-auto-flow:row dense</Code> and <Code>grid-auto-flow: column dense</Code>.
			</P>

			<Usage usage={usage} shortName={shortName} />
			<Grid cols="3" rows="3" justify="stretch" align="stretch" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="start" align="start" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="center" align="start" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="end" align="start" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="start" align="center" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="center" align="center" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="end" align="center" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="start" align="end" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="center" align="end" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>
			<Grid cols="3" rows="3" justify="end" align="end" gapX="6" gapY="6" className="mt-4 h-96 rounded-md bg-slate-200 p-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">1</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">2</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">3</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">4</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">5</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">6</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">7</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">8</div>
				<div className="flex h-10 w-10 items-center justify-center rounded-sm bg-slate-600 text-white">9</div>
			</Grid>

			<Source fileName={fileName} source={source} />
		</>
	)
}
export default HeadingRoute
